/**
 * Storage utilities for Lab Phlebotomy module
 * HIPAA Compliance: Uses encrypted storage for PHI
 * Regulation: 45 CFR 164.312(a)(2)(iv) - Encryption and Decryption
 */

import secureStorage from '../../../utils/security/secureStorage';

const STORAGE_KEY = "labPhlebotomyRecords";

/**
 * Get all lab phlebotomy records from secure storage
 */
export const getLabPhlebotomyRecords = () => {
  try {
    const data = secureStorage.getItem(STORAGE_KEY);
    return data || [];
  } catch (error) {
    // HIPAA: Don't log error details that might contain PHI
    console.error("Error reading lab phlebotomy records");
    return [];
  }
};

/**
 * Save all records to secure storage
 */
const saveRecords = (records) => {
  try {
    secureStorage.setItem(STORAGE_KEY, records);
  } catch (error) {
    console.error("Error saving lab phlebotomy records");
  }
};

/**
 * Create a new lab phlebotomy record
 */
export const createLabPhlebotomyRecord = (record) => {
  const records = getLabPhlebotomyRecords();
  const newRecord = {
    ...record,
    id: `LAB-${Date.now()}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  records.push(newRecord);
  saveRecords(records);
  return newRecord;
};

/**
 * Update an existing lab phlebotomy record
 */
export const updateLabPhlebotomyRecord = (id, updatedData) => {
  const records = getLabPhlebotomyRecords();
  const index = records.findIndex((r) => r.id === id);
  if (index !== -1) {
    records[index] = {
      ...records[index],
      ...updatedData,
      updatedAt: new Date().toISOString(),
    };
    saveRecords(records);
    return records[index];
  }
  return null;
};

/**
 * Delete a lab phlebotomy record
 */
export const deleteLabPhlebotomyRecord = (id) => {
  const records = getLabPhlebotomyRecords();
  const filtered = records.filter((r) => r.id !== id);
  saveRecords(filtered);
  return filtered;
};

/**
 * Get a single record by ID
 */
export const getLabPhlebotomyRecordById = (id) => {
  const records = getLabPhlebotomyRecords();
  return records.find((r) => r.id === id) || null;
};

/**
 * Get counts by medical center
 */
export const getCountsByMedicalCenter = () => {
  const records = getLabPhlebotomyRecords();
  const counts = { All: records.length };

  records.forEach((record) => {
    if (record.medicalCenter) {
      counts[record.medicalCenter] = (counts[record.medicalCenter] || 0) + 1;
    }
  });

  return counts;
};
